import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTE } from '@/config';
import { useVerifyOtp } from '@/hooksApi';
import { useUserStore } from '@/stores';
import { useAuthStore } from '@/stores/authStore';
import { convertArabicToEnglishNumbers } from '@/utils';

/** Digits the backend issues. Kept here so the UI never hard-codes a 4. */
export const OTP_LENGTH = 4;

const sanitize = (value: string) =>
  convertArabicToEnglishNumbers(value)
    .replace(/\D/g, '')
    .slice(0, OTP_LENGTH);

/**
 * OTP entry as a single string rather than an array of per-box refs.
 *
 * That shape is what lets a pasted SMS code, browser one-time-code autofill and
 * arrow-key editing all work — they hand over a whole string at once, which the
 * old one-input-per-digit version had no way to accept.
 */
const useVerify = () => {
  const [code, setCode] = useState('');
  const [failed, setFailed] = useState(false);
  const navigate = useNavigate();
  const setToken = useAuthStore((state) => state.setToken);
  const hash = useUserStore((state) => state.hash);
  const { mutate: verifyOtp, isLoading } = useVerifyOtp();

  // A full code auto-submits, so guard against the same one going twice
  // (re-render, a second onChange, a stray Enter on the button).
  const inFlight = useRef(false);

  const submit = useCallback(
    (value: string) => {
      if (value.length !== OTP_LENGTH || !hash || inFlight.current) return;
      inFlight.current = true;

      verifyOtp(
        { hash, password: value },
        {
          onSuccess: (data) => {
            inFlight.current = false;
            setToken(data?.object?.access_token);
            navigate(ROUTE.HOME); // Home re-routes based on permissions.
          },
          onError: () => {
            inFlight.current = false;
            setFailed(true);
            setCode(''); // Clear so the next attempt starts from an empty box.
          },
        },
      );
    },
    [hash, navigate, setToken, verifyOtp],
  );

  /** Accepts raw input (including Arabic-Indic digits) and submits once full. */
  const changeCode = useCallback(
    (next: string) => {
      const clean = sanitize(next);
      setFailed(false);
      setCode(clean);
      if (clean.length === OTP_LENGTH) submit(clean);
    },
    [submit],
  );

  return {
    code,
    changeCode,
    submit: () => submit(code),
    failed,
    isLoading,
    isComplete: code.length === OTP_LENGTH,
  };
};

export default useVerify;
